import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { getCampaignProgress } from "../../DataTransform";
import { Response } from "../../types";

export interface Props {
  responses?: Response[];
  submitted?: number;
  total?: number;
}

function decimalToHex(d: number, padding: number) {
  d = Math.floor(between(d, 0, 255));
  let hex = Number(d).toString(16);
  padding =
    typeof padding === "undefined" || padding === null
      ? (padding = 2)
      : padding;

  while (hex.length < padding) {
    hex = "0" + hex;
  }

  return hex;
}

function between(num: number, min: number, max: number) {
  return Math.max(min, Math.min(num, max));
}
export function getColor(proportion: number) {
  let red = 0;
  let green = 0;
  const blue = 0;
  if (proportion < 0.5) {
    red = 255;
    green = proportion * 2 * 256;
  } else {
    red = (1 - proportion) * 2 * 256;
    green = 255;
  }
  return `#${decimalToHex(red, 2)}${decimalToHex(green, 2)}${decimalToHex(
    blue,
    2,
  )}`;
}

export const CampaignProgress = ({
  responses = [],
  submitted,
  total,
}: Props) => {
  const progress =
    submitted !== undefined && total !== undefined
      ? { completed: submitted, total }
      : getCampaignProgress(responses);
  // Guard total === 0: NaN here becomes aria-valuenow="NaN" on the
  // progressbar (axe aria-valid-attr-value, critical) and an invalid SVG arc.
  const isEmpty = progress.total <= 0;
  const proportion = isEmpty ? 0 : progress.completed / progress.total;

  // An empty campaign draws a full dim ring (a "track") instead of a 0% arc:
  // value=0 renders no circle at all, which reads as a missing element. The
  // dim full ring keeps the gauge visible without implying progress. (Before
  // the NaN guard this case accidentally rendered a full primary-colored
  // ring — i.e. it looked 100% complete.)
  return (
    <Box sx={{ display: "flex", justifyContent: "left" }}>
      <CircularProgress
        variant="determinate"
        value={isEmpty ? 100 : 100 * proportion}
        aria-label={`${progress.completed} of ${progress.total} responses completed`}
        // The empty-state ring draws full (value=100) but must announce 0.
        // Spread conditionally: an explicit undefined would still override
        // MUI's computed aria-valuenow on the normal rings.
        {...(isEmpty ? { "aria-valuenow": 0 } : {})}
        sx={{
          mr: 1,
          color: isEmpty ? "rgba(255, 255, 255, 0.25)" : getColor(proportion),
        }}
      />
      <Typography sx={{ lineHeight: "40px", verticalAlign: "middle" }}>
        {progress.completed} / {progress.total} Completed
      </Typography>
    </Box>
  );
};
